import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import CustomModal from "../../../base-components/CustomModal";
import Popup from "../../../base-components/Popup";
import TablePaginationActions from "../../../base-components/TablePaginationActions";
import { getSessionUser, hasPermission, isCompanyAdmin, isDynamicStaffRole } from "../../../utils/roleAccess";
import { PageHeader } from "../../../ui";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const selectClass =
  "border border-gx-navy/20 bg-gx-canvas w-full min-h-[44px] px-3 py-2.5 pr-10 rounded-gx-control outline-0 text-sm text-gx-ink appearance-none bg-[length:12px_8px] bg-[right_0.85rem_center] bg-no-repeat bg-[url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%228%22 viewBox=%220 0 12 8%22 fill=%22none%22%3E%3Cpath d=%22M1 1.5L6 6.5L11 1.5%22 stroke=%22%235B676F%22 stroke-width=%221.5%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22/%3E%3C/svg%3E')] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gx-red disabled:opacity-60";
const inputClass =
  "border border-gx-navy/20 bg-gx-canvas w-full min-h-[44px] px-3 py-2.5 rounded-gx-control outline-0 text-sm text-gx-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gx-red";
const labelClass = "block text-sm font-medium text-gx-ink mb-1.5";
const modalHeaderClass = "px-6 pr-14 pb-4 pt-5 border-b border-gx-border";
const modalBodyClass = "px-6 py-5 text-gx-ink";
const modalFooterClass = "flex justify-end gap-3 pt-5 mt-6 border-t border-gx-border";

function CompanyAdminUsers() {
  const sessionUser = useMemo(() => getSessionUser(), []);
  const actingUserId = sessionUser?._id;
  const companyId = sessionUser?.company_id;

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    location_id: "",
  });

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const showPopup = (success, message) => {
    setIsSuccessAlert(success);
    setPopupMessage(message);
    setIsPopupOpen(true);
    setTimeout(() => setIsPopupOpen(false), 4000);
  };

  const loadUsers = useCallback(async () => {
    if (!actingUserId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ actingUserId });
      if (query.trim()) params.set("q", query.trim());
      const res = await fetch(`${BASE_URL}company-admin/users?${params}`);
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to load users");
        return;
      }
      setUsers(data.users || []);
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [actingUserId, query]);

  useEffect(() => {
    if (!actingUserId) return;
    loadUsers();
    fetch(`${BASE_URL}company-admin/roles?actingUserId=${actingUserId}`)
      .then((r) => r.json())
      .then((data) => setRoles((data.roles || []).filter((r) => r.name !== "Member" && r.name !== "User")))
      .catch((err) => console.error(err));
    if (companyId) {
      fetch(`${BASE_URL}locations/getLocationsByCompanyID/${companyId}`)
        .then((r) => r.json())
        .then((data) => setLocations(Array.isArray(data) ? data : data.locations || []))
        .catch((err) => console.error(err));
    }
  }, [actingUserId, companyId, loadUsers]);

  const assignableRoles = useMemo(
    () =>
      roles.filter((r) => {
        if (r.is_system) return ["Admin", "ClubAdmin", "Trainer"].includes(r.name);
        return Boolean(r.company_id);
      }),
    [roles]
  );

  const needsLocationScope = (roleName) => {
    if (!roleName) return false;
    if (roleName === "ClubAdmin") return true;
    const roleDoc = roles.find((r) => r.name === roleName);
    if (roleDoc?.is_dynamic) return true;
    return isDynamicStaffRole(roleName, roleDoc);
  };

  const resetForm = () => {
    setForm({ name: "", email: "", password: "", role: "", location_id: "" });
    setSelectedUser(null);
  };

  const openCreate = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "",
      location_id: user.location_id?._id || user.location_id || "",
    });
    setIsRoleModalOpen(true);
  };

  const openDelete = (user) => {
    setSelectedUser(user);
    setIsDeleteOpen(true);
  };

  const handleCreate = async () => {
    if (!actingUserId || !form.name || !form.email || !form.password || !form.role) {
      showPopup(false, "Name, email, password, and role are required");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}company-admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          actingUserId,
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
          location_id: form.location_id || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to create user");
      } else {
        showPopup(true, "User created successfully");
        setIsCreateOpen(false);
        resetForm();
        loadUsers();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRole = async () => {
    if (!actingUserId || !selectedUser || !form.role) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}company-admin/users/${selectedUser._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          actingUserId,
          role: form.role,
          location_id: form.location_id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to update user");
      } else {
        showPopup(true, "User updated successfully");
        setIsRoleModalOpen(false);
        resetForm();
        loadUsers();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!actingUserId || !selectedUser) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${BASE_URL}company-admin/users/${selectedUser._id}?actingUserId=${actingUserId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ actingUserId }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to delete user");
      } else {
        showPopup(true, "User deleted successfully");
        setIsDeleteOpen(false);
        resetForm();
        loadUsers();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to delete user");
    } finally {
      setSaving(false);
    }
  };

  const paginated =
    rowsPerPage > 0
      ? users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : users;

  if (!isCompanyAdmin(sessionUser?.role)) {
    return (
      <div className="m-5 bg-gx-border p-5 rounded-lg">
        <h3 className="text-xl font-bold text-center text-gx-ink-soft">
          Company Admin access required
        </h3>
      </div>
    );
  }

  const RoleAndLocationFields = ({ idPrefix = "company-admin-user" }) => {
    const locationRequired = needsLocationScope(form.role);
    return (
      <div className="flex flex-col gap-6">
        <div>
          <label className={labelClass} htmlFor={`${idPrefix}-role`}>
            Role
          </label>
          <select
            id={`${idPrefix}-role`}
            className={selectClass}
            value={form.role}
            onChange={(e) =>
              setForm({
                ...form,
                role: e.target.value,
                location_id: needsLocationScope(e.target.value) ? form.location_id : "",
              })
            }
          >
            <option value="">Select role</option>
            {assignableRoles.map((r) => (
              <option key={r._id} value={r.name}>
                {r.name}
                {r.is_system ? " (system)" : ""}
              </option>
            ))}
          </select>
        </div>
        {locationRequired ? (
          <div>
            <label className={labelClass} htmlFor={`${idPrefix}-location`}>
              Location <span className="text-gx-red" aria-hidden="true">*</span>
            </label>
            <select
              id={`${idPrefix}-location`}
              className={selectClass}
              value={form.location_id}
              onChange={(e) => setForm({ ...form, location_id: e.target.value })}
              required
            >
              <option value="">Select location</option>
              {locations.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.name}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs leading-relaxed text-gx-ink-soft">
              Required for ClubAdmin and custom location-scoped roles.
            </p>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div className="m-5">
        <PageHeader
          title="User Management"
          subtitle="Staff and admin accounts only (not members). Assign roles and locations here."
          actions={
            hasPermission(getSessionUser(), "admin_users:create") ? (
            <button className="gx-cta" onClick={openCreate}>
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add User
            </button>
            ) : null
          }
        />
      </div>

      <div className="m-5 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm text-gx-ink-soft">Name or Email</label>
          <input
            type="text"
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0"
            placeholder="Search by name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadUsers()}
          />
        </div>
        <button className="gx-cta" onClick={loadUsers} disabled={loading}>
          {loading ? "Searching..." : "Search"}
        </button>
      </div>

      {users.length > 0 ? (
        <Box className="bg-gx-surface rounded-gx border border-gx-border shadow-gx m-5">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((user) => (
                <TableRow hover key={user._id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.location_id?.name || "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        className="text-gx-ink font-bold hover:text-gx-red"
                        onClick={() => openRoleModal(user)}
                      >
                        <FontAwesomeIcon icon={faPen} /> Edit
                      </button>
                      {String(user._id) !== String(sessionUser._id) && (
                        <button
                          type="button"
                          className="text-gx-ink font-bold hover:text-red-700"
                          onClick={() => openDelete(user)}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  colSpan={5}
                  count={users.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      ) : (
        <div className="bg-gx-border w-full p-3 mt-5 rounded-lg m-5">
          <h3 className="text-xl font-bold text-center text-gx-ink-soft">
            {loading ? "Loading..." : "No users found"}
          </h3>
        </div>
      )}

      <CustomModal
        isModal={isCreateOpen}
        handleModal={() => setIsCreateOpen(false)}
        size="lg"
        titleId="company-admin-add-user-title"
      >
        <div className={modalHeaderClass}>
          <h1 id="company-admin-add-user-title" className="font-semibold text-2xl text-gx-ink tracking-tight text-balance">
            Add User
          </h1>
          <p className="text-sm text-gx-ink-soft mt-1.5 leading-relaxed">
            Create a staff login and assign a role.
          </p>
        </div>
        <div className={modalBodyClass}>
          <div className="flex flex-col gap-5">
            <div>
              <label className={labelClass} htmlFor="company-admin-create-name">
                Name
              </label>
              <input
                id="company-admin-create-name"
                className={inputClass}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                autoComplete="name"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="company-admin-create-email">
                Email
              </label>
              <input
                id="company-admin-create-email"
                type="email"
                className={inputClass}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="name@company.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="company-admin-create-password">
                Password
              </label>
              <input
                id="company-admin-create-password"
                type="password"
                className={inputClass}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Temporary password"
                autoComplete="new-password"
              />
            </div>
            <RoleAndLocationFields idPrefix="company-admin-create" />
          </div>
          <div className={modalFooterClass}>
            <button
              type="button"
              className="gx-cta-secondary min-h-[44px] px-5"
              onClick={() => setIsCreateOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="gx-cta min-h-[44px] px-5"
              onClick={handleCreate}
              disabled={saving}
            >
              {saving ? "Saving..." : "Create"}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isModal={isRoleModalOpen}
        handleModal={() => setIsRoleModalOpen(false)}
        size="md"
        titleId="company-admin-change-role-title"
      >
        <div className={modalHeaderClass}>
          <h1 id="company-admin-change-role-title" className="font-semibold text-2xl text-gx-ink tracking-tight text-balance">
            Change Role
          </h1>
          <p className="text-sm text-gx-ink-soft mt-1.5 leading-relaxed">
            Update access for this staff account.
          </p>
        </div>
        <div className={modalBodyClass}>
          <div className="mb-6 pb-4 border-b border-gx-border/80">
            <p className="font-semibold text-gx-ink truncate">{selectedUser?.name}</p>
            <p className="text-sm text-gx-ink-soft truncate mt-0.5">{selectedUser?.email}</p>
          </div>
          <RoleAndLocationFields idPrefix="company-admin-role" />
          <div className={modalFooterClass}>
            <button
              type="button"
              className="gx-cta-secondary min-h-[44px] px-5"
              onClick={() => setIsRoleModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="gx-cta min-h-[44px] px-5"
              onClick={handleSaveRole}
              disabled={
                saving ||
                !form.role ||
                (needsLocationScope(form.role) && !form.location_id)
              }
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isModal={isDeleteOpen}
        handleModal={() => setIsDeleteOpen(false)}
        size="md"
        titleId="company-admin-delete-user-title"
      >
        <div className={modalHeaderClass}>
          <h2 id="company-admin-delete-user-title" className="font-semibold text-2xl text-gx-ink tracking-tight text-balance">
            Delete User?
          </h2>
        </div>
        <div className={modalBodyClass}>
          <p className="text-gx-ink-soft leading-relaxed text-pretty">
            Deactivate login for &quot;{selectedUser?.name}&quot;? This is a soft delete.
          </p>
          <div className={modalFooterClass}>
            <button
              type="button"
              className="gx-cta-secondary min-h-[44px] px-5"
              onClick={() => setIsDeleteOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="gx-cta min-h-[44px] px-5"
              onClick={handleDelete}
              disabled={saving}
            >
              {saving ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </CustomModal>

      {isPopupOpen && (
        <Popup
          onClose={() => setIsPopupOpen(false)}
          alertType={isSuccessAlert ? "success" : "failure"}
          message={popupMessage}
        />
      )}
    </>
  );
}

export default CompanyAdminUsers;
